import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { emailApi } from '../../utils/emailApi';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Section, PageHero, SectionTitle, AnimatedBox, GradientButton } from '../../components/ui';

const FormCard = styled(Card)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  borderRadius: '24px',
}));

const ContactInfoCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    transform: 'translateX(8px)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.accent.gradient,

  '& svg': {
    fontSize: '24px',
    color: '#FFFFFF',
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '12px',
  borderRadius: '12px',
  color: '#FFFFFF',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-3px)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.grey[50],
    transition: 'all 0.3s ease',

    '&:hover': {
      backgroundColor: '#FFFFFF',
    },

    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
    },

    '& fieldset': {
      borderColor: 'transparent',
    },

    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },

  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
}));

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await emailApi.sendContact(formData);

      // Track contact event (GA4)
      if (window.gtag) {
        window.gtag('event', 'contact_form_submit', {
          event_category: 'engagement',
          event_label: formData.subject,
        });
      }

      setSnackbar({
        open: true,
        message: t('contact.form.success'),
        severity: 'success',
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSnackbar({
        open: true,
        message: t('contact.form.error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      {/* Page Header */}
      <PageHero title={t('contact.title')} subtitle={t('contact.subtitle')} />

      {/* Contact Form Section */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} lg={7}>
              <AnimatedBox animation="fadeInLeft">
                <FormCard>
                  <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
                      {t('contact.form.title')}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.name')}
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.email')}
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.company')}
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.phone')}
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.subject')}
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label={t('contact.form.message')}
                            name="message"
                            multiline
                            rows={5}
                            value={formData.message}
                            onChange={handleChange}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <GradientButton
                            type="submit"
                            size="large"
                            disabled={loading}
                            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{ minWidth: '200px' }}
                          >
                            {loading ? t('common.sending') : t('contact.form.submit')}
                          </GradientButton>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </FormCard>
              </AnimatedBox>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} lg={5}>
              <AnimatedBox animation="fadeInRight">
                <Box
                  sx={{
                    background: (theme) => theme.palette.background.hero,
                    borderRadius: '24px',
                    p: { xs: 3, md: 5 },
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ color: '#FFFFFF', fontWeight: 700, mb: 4 }}
                  >
                    {t('contact.info.title')}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 5 }}>
                    <ContactInfoCard>
                      <IconWrapper>
                        <EmailIcon />
                      </IconWrapper>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}
                        >
                          {t('common.email')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 500 }}
                        >
                          {t('contact.info.email')}
                        </Typography>
                      </Box>
                    </ContactInfoCard>

                    <ContactInfoCard>
                      <IconWrapper>
                        <PhoneIcon />
                      </IconWrapper>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}
                        >
                          {t('common.phone')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 500 }}
                        >
                          {t('contact.info.phone')}
                        </Typography>
                      </Box>
                    </ContactInfoCard>

                    <ContactInfoCard>
                      <IconWrapper>
                        <LocationOnIcon />
                      </IconWrapper>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}
                        >
                          {t('common.address')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 500 }}
                        >
                          {t('contact.info.address')}
                        </Typography>
                      </Box>
                    </ContactInfoCard>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}
                  >
                    {t('footer.followUs')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <SocialButton
                      aria-label="Facebook"
                      component="a"
                      href="https://www.facebook.com/nexfahtechnologysolutions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FacebookIcon />
                    </SocialButton>
                    <SocialButton
                      aria-label="Instagram"
                      component="a"
                      href="https://www.instagram.com/nexfahtechnologysolutions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon />
                    </SocialButton>
                    <SocialButton
                      aria-label="LinkedIn"
                      component="a"
                      href="https://linkedin.com/company/nexfahtechnology"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon />
                    </SocialButton>
                  </Box>
                </Box>
              </AnimatedBox>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Snackbar for form feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
